// -----------------------------------------------------------------------
// <copyright file="PageHeader.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { ITheme, mergeStyles } from '@fluentui/react';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../../web-legacy/models/reduxState';
import {
  isAgaveSelector,
  isAmcSelector,
  isGetHelpSelector,
  isSmbHubSelector,
  modalitySectionStateSelector,
  showModalitiesStickyFooterBRSSelector,
  isSignUpSelector,
} from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { SectionHeader } from '../../../web-legacy/packages/contactUs/presentationalComponents/SectionHeader';
import { updateModalitySectionExpando } from '../../../web-legacy/packages/contactUs/reduxUpdater/actions/modalitySectionActions';
import {
  isNonUnifiedPremierSelector,
  isPremierTenantSelector,
  isUnifiedEnterprisePremierSelector,
  isUnifiedPremierSelector,
} from '../../../web-legacy/packages/contactUs/reduxUpdater/selectors/globalStateSelectors';
import { commonClassNames } from '../../../web-legacy/packages/contactUs/styles/commonStyles';
import {
  getContactUsClassNames,
  getContactUsStyles,
} from '../../../web-legacy/packages/contactUs/styles/contactUsStyles';
import { getString, isRtl } from '../../utils/stringLoader';
import { SupportOptionsHeader } from './SupportOptionsHeader';

export interface IPageHeaderProps {
  theme: ITheme;
  title?: string;
  description?: string;
  descriptionElement?: JSX.Element;
  isOutlookModalityOptionHeader?: true;

  onStartChatClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onKeyDownStartChat?: (event: React.KeyboardEvent<HTMLElement>) => void;
  getChatDescription?: { __html: string };
}

export const PageHeader = (props: IPageHeaderProps): JSX.Element => {
  const hideSelfHelpSmcIvr = useSelector((appState: IAppState) => appState.ContactState.SharedState.hideSelfHelpSmcIvr);
  const isOutlookDotCom = useSelector((appState: IAppState) => appState.ContactState.SharedState.isOutlookDotCom);
  const showModalitiesStickyFooter = useSelector(showModalitiesStickyFooterBRSSelector);
  const modalityState = useSelector(modalitySectionStateSelector);

  const isAmc = useSelector(isAmcSelector);
  const isGetHelp = useSelector(isGetHelpSelector);
  const isAgave = useSelector(isAgaveSelector);
  const isCollapsedModalities = modalityState.isCollapsedModalities;
  const isSmbHub = useSelector(isSmbHubSelector);
  const isSignUp = useSelector(isSignUpSelector);

  const isUnifiedPremier = useSelector(isUnifiedPremierSelector);
  const isUnifiedEnterprisePremier = useSelector(isUnifiedEnterprisePremierSelector);
  const isNonUnifiedPremier = useSelector(isNonUnifiedPremierSelector);
  const isPremierTenantEnabled = useSelector(isPremierTenantSelector);
  const isRenderingModalitySection = modalityState.isRenderingModalitySection;

  const dispatch = useDispatch();
  const dispatchupdateModalitySectionExpando = useCallback(
    (isCollapsedModalities: boolean) => dispatch(updateModalitySectionExpando(isCollapsedModalities)),
    [dispatch]
  );

  const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });

  if (!isRenderingModalitySection || (!isCollapsedModalities && hideSelfHelpSmcIvr)) {
    return null;
  }

  if (isOutlookDotCom) {
    if (props.isOutlookModalityOptionHeader) {
      return (
        <div className={commonClassNames.hOne}>
          <div id="modalitiesHeader" className={mergeStyles(commonClassNames.inline, contactUsClassNames.h2)}>
            {getString('modality_chooseSupportOption')}
          </div>
        </div>
      );
    } else {
      return null;
    }
  }

  const getSupportOptionsHeaderString = (isSignUp: boolean) => {
    if (isSignUp) {
      return getString('result_signUpPageTalkToMicrosoftAdvisor');
    } else {
      return getString('result_getMoreHelpText');
    }
  };

  return (
    <>
      {!showModalitiesStickyFooter && !isAmc && !isSmbHub ? (
        <SupportOptionsHeader
          header={getSupportOptionsHeaderString(isSignUp)}
          isCollapsedModalities={isCollapsedModalities}
          isAgave={isAgave}
          updateModalitySectionExpando={dispatchupdateModalitySectionExpando}
          isRtl={isRtl()}
          theme={props.theme}
          isSignUp={isSignUp}
        />
      ) : (
        <SectionHeader
          title={props.title}
          description={props.description}
          descriptionElement={props.descriptionElement}
          theme={props.theme}
          isUnifiedPremier={isUnifiedPremier}
          isUnifiedEnterprisePremier={isUnifiedEnterprisePremier}
          isNonUnifiedPremier={isNonUnifiedPremier}
          isPremierTenantEnabled={isPremierTenantEnabled}
          largeFrontPageTitle={isGetHelp}
        />
      )}
    </>
  );
};
